'use client';

// ============================================================================
// SOFTWARE AGILE SECTIONS
// Fetch-driven agile context cards for SoftwareOverviewTab:
//   - ParentEpicSection    — resolved child_of epic link + link/unlink picker
//   - SprintMembershipSection — work_item_sprints membership + assign/remove
//   - EpicChildrenSection  — rollup of an epic's child items (child_of)
//
// Writes go through the canonical APIs:
//   /api/v1/software-development/roadmap  (link/unlink — RBAC + activity log)
//   /api/v1/work-items/sprints[/:id/items] (membership — committed points)
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Calendar,
  ChevronDown,
  Flag,
  Layers,
  Loader2,
  Plus,
  Search,
  Star,
  Unlink,
  X,
} from 'lucide-react';
import { authService } from '@/lib/authService';
import { getApiUrl } from '@/lib/apiConfig';
import { notify } from '@/lib/notifications';

// ── Shared types ─────────────────────────────────────────────────────────────

export interface AgileLinkedItem {
  id: string;
  item_type?: string;
  item_number?: string | null;
  title?: string;
  status?: string;
  priority?: string | null;
  assigned_to?: number | null;
  due_date?: string | null;
  custom_fields?: Record<string, unknown> | null;
}

interface SprintMembership {
  id: number;
  name: string;
  status?: string | null;
  goal?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  is_active?: boolean | null;
  member_story_points?: number | null;
  member_added_at?: string | null;
}

interface SprintOption {
  id: number;
  name: string;
  status?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  is_active?: boolean | null;
  items_count?: number | string;
  total_points?: number | string;
}

/** Roadmap child types — mirrors ROADMAP_CHILD_TYPES in
 *  src/lib/software-dev/roadmapService.ts (only these can link under an epic). */
export const ROADMAP_CHILD_ITEM_TYPES = new Set(['feature', 'story', 'task', 'bug', 'request']);

const DONE_STATUSES = new Set(['completed', 'done', 'resolved', 'closed']);
const TERMINAL_STATUSES = new Set([...DONE_STATUSES, 'cancelled', 'canceled', 'archived']);

// ── Shared helpers ───────────────────────────────────────────────────────────

function authHeaders(): Record<string, string> {
  const token = authService.getTokens()?.access_token || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function jsonHeaders(): Record<string, string> {
  return { ...authHeaders(), 'Content-Type': 'application/json' };
}

function parseCustomFields(value: unknown): Record<string, unknown> {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, unknown> : {};
    } catch {
      return {};
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function itemStoryPoints(item: AgileLinkedItem): number {
  const n = Number(parseCustomFields(item.custom_fields).story_points);
  return Number.isFinite(n) ? n : 0;
}


export function formatAgileStatus(status?: string | null): string {
  return (status || 'open').replace(/[_-]/g, ' ');
}

function statusChipClass(status: string | undefined | null, isDark: boolean): string {
  const s = (status || '').toLowerCase();
  if (DONE_STATUSES.has(s)) return isDark ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-50 text-emerald-700';
  if (s === 'in_progress' || s === 'active') return isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700';
  if (s === 'review' || s === 'in_review' || s === 'testing') return isDark ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-50 text-amber-700';
  if (s === 'blocked') return isDark ? 'bg-red-900/40 text-red-300' : 'bg-red-50 text-red-700';
  if (TERMINAL_STATUSES.has(s)) return isDark ? 'bg-gray-800 text-gray-400 line-through' : 'bg-gray-100 text-gray-400 line-through';
  return isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600';
}

function priorityDotClass(priority?: string | null): string {
  switch ((priority || '').toLowerCase()) {
    case 'urgent':
    case 'critical':
      return 'bg-red-500';
    case 'high':
      return 'bg-orange-500';
    case 'low':
      return 'bg-gray-400';
    default:
      return 'bg-yellow-500';
  }
}

function formatShortDate(value?: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
}

function cardClass(isDark: boolean): string {
  return `rounded-xl border p-3 ${isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`;
}

function labelClass(isDark: boolean): string {
  return `text-xs uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`;
}

function inputClass(isDark: boolean): string {
  return `w-full rounded-lg border px-3 py-2 text-sm ${isDark
    ? 'border-gray-700 bg-gray-900 text-gray-100 placeholder-gray-500'
    : 'border-gray-300 bg-white text-gray-900 placeholder-gray-400'}`;
}

function smallButtonClass(isDark: boolean, tone: 'default' | 'primary' | 'danger' = 'default'): string {
  const base = 'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-50';
  if (tone === 'primary') return `${base} ${isDark ? 'bg-indigo-600 text-white hover:bg-indigo-500' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`;
  if (tone === 'danger') return `${base} ${isDark ? 'text-red-300 hover:bg-red-900/40' : 'text-red-600 hover:bg-red-50'}`;
  return `${base} ${isDark ? 'border border-gray-700 text-gray-300 hover:bg-gray-800' : 'border border-gray-300 text-gray-600 hover:bg-gray-100'}`;
}

/** Shared panel container used by the three sections. */
export function AgileSectionCard({
  isDark,
  icon: Icon,
  iconClass,
  title,
  action,
  children,
}: {
  isDark: boolean;
  icon: React.ElementType;
  iconClass: string;
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className={cardClass(isDark)}>
      <div className="flex items-center justify-between gap-2">
        <div className={`flex items-center gap-2 ${labelClass(isDark)}`}>
          <Icon className={`h-3.5 w-3.5 ${iconClass}`} />
          <span className="font-semibold">{title}</span>
        </div>
        {action}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function PickerList<T>({
  isDark,
  items,
  loading,
  emptyText,
  renderItem,
}: {
  isDark: boolean;
  items: T[];
  loading: boolean;
  emptyText: string;
  renderItem: (item: T) => React.ReactNode;
}) {
  if (loading) {
    return (
      <div className={`flex items-center gap-2 px-3 py-4 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
      </div>
    );
  }
  if (items.length === 0) {
    return <div className={`px-3 py-4 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{emptyText}</div>;
  }
  return <div className="max-h-56 overflow-auto">{items.map(renderItem)}</div>;
}

// ── Parent epic ──────────────────────────────────────────────────────────────

/**
 * Shows the resolved child_of epic for a roadmap child item and lets the user
 * link/relink/unlink it. Non-epic child_of parents are surfaced read-only —
 * roadmap link refuses to repoint operational relationships by design.
 */
export function ParentEpicSection({
  workItemId,
  itemType,
  isDark,
  onOpenItem,
}: {
  workItemId: string;
  itemType: string;
  isDark: boolean;
  onOpenItem?: (itemId: string) => void;
}) {
  const canLink = ROADMAP_CHILD_ITEM_TYPES.has(itemType);
  const [parent, setParent] = useState<AgileLinkedItem | null>(null);
  const [nonEpicParent, setNonEpicParent] = useState<AgileLinkedItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [epicOptions, setEpicOptions] = useState<AgileLinkedItem[]>([]);
  const [epicsLoading, setEpicsLoading] = useState(false);
  const [epicSearch, setEpicSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadParent = useCallback(async () => {
    if (!workItemId) return;
    setLoading(true);
    try {
      const res = await fetch(getApiUrl(`/api/v1/work-items/${workItemId}/relationships`), { headers: authHeaders() });
      const data = await res.json().catch(() => null);
      if (!mountedRef.current) return;
      if (res.ok && data?.success) {
        const outgoingChildren: Array<{ item?: AgileLinkedItem }> =
          data?.data?.relationships?.outgoing?.child_of || [];
        const epicRel = outgoingChildren.find((rel) => rel.item?.item_type === 'epic');
        const otherRel = outgoingChildren.find((rel) => rel.item && rel.item.item_type !== 'epic');
        setParent(epicRel?.item || null);
        setNonEpicParent(epicRel ? null : otherRel?.item || null);
      }
    } catch {
      // silent — parent context is best-effort
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [workItemId]);

  useEffect(() => { void loadParent(); }, [loadParent]);

  const loadEpicOptions = useCallback(async () => {
    setEpicsLoading(true);
    try {
      const res = await fetch(
        getApiUrl('/api/v1/software-development/roadmap?view=items&item_types=epic&limit=200'),
        { headers: authHeaders() },
      );
      const data = await res.json().catch(() => null);
      if (mountedRef.current && res.ok && data?.success) {
        setEpicOptions(Array.isArray(data.data) ? data.data : []);
      }
    } catch {
      // silent
    } finally {
      if (mountedRef.current) setEpicsLoading(false);
    }
  }, []);

  const openPicker = () => {
    setPickerOpen(true);
    if (epicOptions.length === 0) void loadEpicOptions();
  };

  const roadmapAction = async (body: Record<string, unknown>): Promise<boolean> => {
    const res = await fetch(getApiUrl('/api/v1/software-development/roadmap'), {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data?.success) {
      notify.error(data?.error || 'Roadmap update failed');
      return false;
    }
    return true;
  };

  const handleLink = async (epicId: string) => {
    if (busy) return;
    setBusy(true);
    const ok = await roadmapAction({ action: 'link', child_id: workItemId, parent_id: epicId });
    if (ok) {
      notify.success('Linked to epic');
      setPickerOpen(false);
      await loadParent();
    }
    setBusy(false);
  };

  const handleUnlink = async () => {
    if (busy) return;
    setBusy(true);
    const ok = await roadmapAction({ action: 'unlink', child_id: workItemId });
    if (ok) {
      notify.success('Removed from epic');
      await loadParent();
    }
    setBusy(false);
  };

  const filteredEpics = useMemo(() => {
    const term = epicSearch.trim().toLowerCase();
    const options = epicOptions.filter((epic) => epic.id !== parent?.id);
    if (!term) return options;
    return options.filter((epic) =>
      (epic.title || '').toLowerCase().includes(term)
      || (epic.item_number || '').toLowerCase().includes(term));
  }, [epicOptions, epicSearch, parent?.id]);

  if (loading) {
    return (
      <AgileSectionCard isDark={isDark} icon={Layers} iconClass="text-purple-500" title="Parent epic">
        <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking epic link…
        </div>
      </AgileSectionCard>
    );
  }

  return (
    <AgileSectionCard
      isDark={isDark}
      icon={Layers}
      iconClass="text-purple-500"
      title="Parent epic"
      action={
        canLink ? (
          parent ? (
            <button type="button" onClick={handleUnlink} disabled={busy} className={smallButtonClass(isDark, 'danger')}>
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Unlink className="h-3 w-3" />}
              Unlink
            </button>
          ) : (
            <button type="button" onClick={openPicker} className={smallButtonClass(isDark)}>
              <Plus className="h-3 w-3" /> Link to epic
            </button>
          )
        ) : undefined
      }
    >
      {parent ? (
        <button
          type="button"
          onClick={() => parent.id && onOpenItem?.(parent.id)}
          className={`w-full rounded-lg border px-3 py-2 text-left transition-colors ${isDark
            ? 'border-purple-900/60 bg-purple-900/10 hover:bg-purple-900/20'
            : 'border-purple-200 bg-purple-50/60 hover:bg-purple-50'}`}
        >
          <div className="flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${priorityDotClass(parent.priority)}`} />
            <span className={`font-mono text-xs ${isDark ? 'text-purple-300' : 'text-purple-600'}`}>
              {parent.item_number || 'EPIC'}
            </span>
            <span className={`flex-1 truncate text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
              {parent.title}
            </span>
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium capitalize ${statusChipClass(parent.status, isDark)}`}>
              {formatAgileStatus(parent.status)}
            </span>
          </div>
        </button>
      ) : (
        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {nonEpicParent ? (
            <span>
              Linked under <span className="font-medium">{nonEpicParent.item_number || nonEpicParent.item_type}</span>
              {' '}<span className={isDark ? 'text-gray-500' : 'text-gray-400'}>({nonEpicParent.title})</span> — not an epic.
            </span>
          ) : canLink ? (
            'Not part of an epic yet. Link it so it rolls up on the roadmap.'
          ) : (
            'This item type cannot be linked under a roadmap epic.'
          )}
        </div>
      )}

      {pickerOpen && (
        <div className={`mt-3 overflow-hidden rounded-lg border ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
          <div className={`flex items-center gap-2 border-b px-3 py-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <Search className={`h-3.5 w-3.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              autoFocus
              value={epicSearch}
              onChange={(e) => setEpicSearch(e.target.value)}
              placeholder="Search epics by number or title…"
              className={`w-full bg-transparent text-sm outline-none ${isDark ? 'text-gray-100 placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}`}
            />
            <button type="button" onClick={() => setPickerOpen(false)} className={isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}>
              <X className="h-4 w-4" />
            </button>
          </div>
          <PickerList
            isDark={isDark}
            items={filteredEpics}
            loading={epicsLoading}
            emptyText="No epics found."
            renderItem={(epic) => (
              <button
                key={epic.id}
                type="button"
                disabled={busy}
                onClick={() => handleLink(epic.id)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}
              >
                <Layers className="h-4 w-4 text-purple-500" />
                <span className={`font-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{epic.item_number}</span>
                <span className={`flex-1 truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{epic.title}</span>
                <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium capitalize ${statusChipClass(epic.status, isDark)}`}>
                  {formatAgileStatus(epic.status)}
                </span>
              </button>
            )}
          />
        </div>
      )}
    </AgileSectionCard>
  );
}

// ── Sprint membership ────────────────────────────────────────────────────────

/**
 * Real sprint membership (work_item_sprint_items) with assign/remove —
 * replaces the legacy free-text sprint_name custom field.
 */
export function SprintMembershipSection({
  workItemId,
  itemType,
  isDark,
  storyPoints,
}: {
  workItemId: string;
  itemType: string;
  isDark: boolean;
  storyPoints?: number;
}) {
  const [memberships, setMemberships] = useState<SprintMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [sprintOptions, setSprintOptions] = useState<SprintOption[]>([]);
  const [sprintsLoading, setSprintsLoading] = useState(false);
  const [sprintSearch, setSprintSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadMemberships = useCallback(async () => {
    if (!workItemId) return;
    setLoading(true);
    try {
      const res = await fetch(
        getApiUrl(`/api/v1/work-items/sprints?work_item_id=${encodeURIComponent(workItemId)}`),
        { headers: authHeaders() },
      );
      const data = await res.json().catch(() => null);
      if (mountedRef.current && res.ok && data?.success) {
        setMemberships(Array.isArray(data.data) ? data.data : []);
      }
    } catch {
      // silent
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [workItemId]);

  useEffect(() => { void loadMemberships(); }, [loadMemberships]);

  const loadSprintOptions = useCallback(async () => {
    setSprintsLoading(true);
    try {
      const res = await fetch(getApiUrl('/api/v1/work-items/sprints'), { headers: authHeaders() });
      const data = await res.json().catch(() => null);
      if (mountedRef.current && res.ok && data?.success) {
        setSprintOptions(Array.isArray(data.data) ? data.data : []);
      }
    } catch {
      // silent
    } finally {
      if (mountedRef.current) setSprintsLoading(false);
    }
  }, []);

  const openPicker = () => {
    setPickerOpen(true);
    if (sprintOptions.length === 0) void loadSprintOptions();
  };

  const handleAssign = async (sprint: SprintOption) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(getApiUrl(`/api/v1/work-items/sprints/${sprint.id}/items`), {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({
          work_item_id: workItemId,
          story_points: Number.isFinite(storyPoints) ? storyPoints : null,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        const conflict = Array.isArray(data?.conflicts) && data.conflicts[0]?.sprint_name
          ? ` (already in ${data.conflicts[0].sprint_name})`
          : '';
        notify.error(`${data?.error || 'Could not add to sprint'}${conflict}`);
      } else {
        notify.success(`Added to ${sprint.name}`);
        setPickerOpen(false);
        await loadMemberships();
      }
    } catch {
      notify.error('Could not add to sprint');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (sprint: SprintMembership) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(
        getApiUrl(`/api/v1/work-items/sprints/${sprint.id}/items?work_item_id=${encodeURIComponent(workItemId)}`),
        { method: 'DELETE', headers: authHeaders() },
      );
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        notify.error(data?.error || 'Could not remove from sprint');
      } else {
        notify.success(`Removed from ${sprint.name}`);
        await loadMemberships();
      }
    } catch {
      notify.error('Could not remove from sprint');
    } finally {
      setBusy(false);
    }
  };

  const filteredSprints = useMemo(() => {
    const memberIds = new Set(memberships.map((m) => m.id));
    const term = sprintSearch.trim().toLowerCase();
    const assignable = sprintOptions
      .filter((s) => !memberIds.has(s.id))
      .sort((a, b) => {
        const rank = (s: SprintOption) => {
          const st = (s.status || '').toLowerCase();
          if (st === 'active') return 0;
          if (st === 'planning') return 1;
          return 2;
        };
        return rank(a) - rank(b);
      });
    if (!term) return assignable;
    return assignable.filter((s) => s.name.toLowerCase().includes(term));
  }, [memberships, sprintOptions, sprintSearch]);

  const sprintWindow = (s: { start_date?: string | null; end_date?: string | null }) => {
    const start = formatShortDate(s.start_date);
    const end = formatShortDate(s.end_date);
    return start && end ? `${start} – ${end}` : null;
  };

  return (
    <AgileSectionCard
      isDark={isDark}
      icon={Calendar}
      iconClass="text-cyan-500"
      title="Sprint"
      action={
        <button type="button" onClick={openPicker} className={smallButtonClass(isDark)}>
          <Plus className="h-3 w-3" /> Add to sprint
        </button>
      }
    >
      {loading ? (
        <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking sprints…
        </div>
      ) : memberships.length === 0 ? (
        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Not scheduled in a sprint.
        </div>
      ) : (
        <div className="space-y-2">
          {memberships.map((sprint) => (
            <div
              key={sprint.id}
              className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${isDark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-white'}`}
            >
              <Flag className={`h-4 w-4 ${(sprint.status || '').toLowerCase() === 'active' ? 'text-emerald-500' : 'text-cyan-500'}`} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className={`truncate text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{sprint.name}</span>
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium capitalize ${statusChipClass(sprint.status, isDark)}`}>
                    {formatAgileStatus(sprint.status)}
                  </span>
                </div>
                <div className={`mt-0.5 flex items-center gap-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {sprintWindow(sprint) && <span>{sprintWindow(sprint)}</span>}
                  {sprint.member_story_points != null && (
                    <span className="inline-flex items-center gap-1"><Star className="h-3 w-3" />{sprint.member_story_points} pts</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRemove(sprint)}
                disabled={busy}
                title="Remove from sprint"
                className={smallButtonClass(isDark, 'danger')}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {pickerOpen && (
        <div className={`mt-3 overflow-hidden rounded-lg border ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
          <div className={`flex items-center gap-2 border-b px-3 py-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <Search className={`h-3.5 w-3.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              autoFocus
              value={sprintSearch}
              onChange={(e) => setSprintSearch(e.target.value)}
              placeholder="Search sprints…"
              className={`w-full bg-transparent text-sm outline-none ${isDark ? 'text-gray-100 placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}`}
            />
            <button type="button" onClick={() => setPickerOpen(false)} className={isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}>
              <X className="h-4 w-4" />
            </button>
          </div>
          <PickerList
            isDark={isDark}
            items={filteredSprints}
            loading={sprintsLoading}
            emptyText="No sprints available. Create one in Sprint Planning."
            renderItem={(sprint) => (
              <button
                key={sprint.id}
                type="button"
                disabled={busy}
                onClick={() => handleAssign(sprint)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}
              >
                <Calendar className="h-4 w-4 text-cyan-500" />
                <span className={`flex-1 truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{sprint.name}</span>
                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{sprintWindow(sprint)}</span>
                <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium capitalize ${statusChipClass(sprint.status, isDark)}`}>
                  {formatAgileStatus(sprint.status)}
                </span>
              </button>
            )}
          />
        </div>
      )}
    </AgileSectionCard>
  );
}

// ── Epic children rollup ─────────────────────────────────────────────────────

/**
 * Epic delivery rollup: children via child_of with a status progress bar,
 * per-type counts, story-point totals and a click-through child list.
 */
export function EpicChildrenSection({
  workItemId,
  isDark,
  onOpenSubtask,
}: {
  workItemId: string;
  isDark: boolean;
  onOpenSubtask?: (itemId: string) => void;
}) {
  const [children, setChildren] = useState<AgileLinkedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          getApiUrl(`/api/v1/work-items?parent_id=${encodeURIComponent(workItemId)}&limit=200`),
          { headers: authHeaders() },
        );
        const data = await res.json().catch(() => null);
        if (!cancelled && res.ok && data?.success !== false) {
          const list: AgileLinkedItem[] = Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data?.data)
              ? data.data
              : [];
          setChildren(list);
        }
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [workItemId]);

  const rollup = useMemo(() => {
    const byType = new Map<string, number>();
    const byStatus = new Map<string, number>();
    let done = 0;
    let points = 0;
    let donePoints = 0;
    for (const child of children) {
      const type = (child.item_type || 'task').toLowerCase();
      byType.set(type, (byType.get(type) || 0) + 1);
      const status = (child.status || 'open').toLowerCase();
      byStatus.set(status, (byStatus.get(status) || 0) + 1);
      const pts = itemStoryPoints(child);
      points += pts;
      if (DONE_STATUSES.has(status) || TERMINAL_STATUSES.has(status)) {
        done += 1;
        donePoints += pts;
      }
    }
    const percent = children.length ? Math.round((done / children.length) * 100) : 0;
    return { byType, byStatus, done, points, donePoints, percent };
  }, [children]);

  const visibleChildren = expanded ? children : children.slice(0, 8);

  const statusSegments = useMemo(() => {
    const groups: Array<{ key: string; count: number; color: string }> = [
      { key: 'done', count: 0, color: 'bg-emerald-500' },
      { key: 'active', count: 0, color: 'bg-blue-500' },
      { key: 'review', count: 0, color: 'bg-amber-500' },
      { key: 'open', count: 0, color: isDark ? 'bg-gray-600' : 'bg-gray-300' },
      { key: 'cancelled', count: 0, color: isDark ? 'bg-gray-800' : 'bg-gray-200' },
    ];
    for (const [status, count] of rollup.byStatus) {
      if (DONE_STATUSES.has(status)) groups[0].count += count;
      else if (status === 'in_progress' || status === 'active') groups[1].count += count;
      else if (status === 'review' || status === 'in_review' || status === 'testing' || status === 'blocked') groups[2].count += count;
      else if (status === 'cancelled' || status === 'canceled' || status === 'archived') groups[4].count += count;
      else groups[3].count += count;
    }
    return groups.filter((g) => g.count > 0);
  }, [rollup.byStatus, isDark]);

  return (
    <AgileSectionCard
      isDark={isDark}
      icon={Layers}
      iconClass="text-purple-500"
      title={`Epic delivery${children.length ? ` · ${children.length} items` : ''}`}
      action={
        children.length > 0 ? (
          <span className={`text-xs font-semibold ${isDark ? 'text-emerald-300' : 'text-emerald-600'}`}>
            {rollup.percent}% done
          </span>
        ) : undefined
      }
    >
      {loading ? (
        <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading children…
        </div>
      ) : children.length === 0 ? (
        <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          No child items linked yet. Add stories, tasks or bugs to see the rollup.
        </div>
      ) : (
        <>
          {/* Progress bar */}
          <div className={`flex h-2 w-full overflow-hidden rounded-full ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`}>
            {statusSegments.map((seg) => (
              <div
                key={seg.key}
                className={seg.color}
                style={{ width: `${(seg.count / children.length) * 100}%` }}
              />
            ))}
          </div>

          {/* Type + points summary */}
          <div className={`mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {Array.from(rollup.byType.entries()).map(([type, count]) => (
              <span key={type} className="capitalize">{count} {type}{count === 1 ? '' : 's'}</span>
            ))}
            {rollup.points > 0 && (
              <span className="inline-flex items-center gap-1">
                <Star className="h-3 w-3" />{rollup.donePoints}/{rollup.points} pts done
              </span>
            )}
            <span>{rollup.done}/{children.length} complete</span>
          </div>

          {/* Child list */}
          <div className={`mt-3 divide-y rounded-lg border ${isDark ? 'divide-gray-800 border-gray-700' : 'divide-gray-100 border-gray-200'}`}>
            {visibleChildren.map((child) => {
              const pts = itemStoryPoints(child);
              return (
                <button
                  key={child.id}
                  type="button"
                  onClick={() => onOpenSubtask?.(child.id)}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}
                >
                  <span className={`h-2 w-2 shrink-0 rounded-full ${priorityDotClass(child.priority)}`} />
                  <span className={`font-mono text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{child.item_number}</span>
                  <span className={`min-w-0 flex-1 truncate text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{child.title}</span>
                  {pts > 0 && (
                    <span className={`inline-flex items-center gap-0.5 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      <Star className="h-3 w-3" />{pts}
                    </span>
                  )}
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium capitalize ${statusChipClass(child.status, isDark)}`}>
                    {formatAgileStatus(child.status)}
                  </span>
                </button>
              );
            })}
          </div>

          {children.length > 8 && (
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              className={`mt-2 inline-flex items-center gap-1 text-xs font-medium ${isDark ? 'text-indigo-300 hover:text-indigo-200' : 'text-indigo-600 hover:text-indigo-700'}`}
            >
              <ChevronDown className={`h-3 w-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
              {expanded ? 'Show less' : `Show all ${children.length} items`}
            </button>
          )}
        </>
      )}
    </AgileSectionCard>
  );
}

